<template>
  <div class="questions-single inside-banner mobile-top collab">
    <!-- ===== Header (same banner as the list page) ===== -->
    <banner-4>
      <div class="thread-head">
        <router-link :to="backLink" class="thread-head__back">
          <span class="material-icons" aria-hidden="true">arrow_back</span>
          All discussions
        </router-link>

        <template v-if="loading">
          <span class="sk sk--on-dark sk--pill"></span>
          <span class="sk sk--on-dark sk--h1"></span>
        </template>

        <template v-else-if="original">
          <h1 class="thread-head__title">{{ original.subject }}</h1>
          <p class="thread-head__meta">
            Started by <strong>{{ original.author }}</strong>
            <span aria-hidden="true">·</span>
            <time :datetime="original.postedIso" :title="fullDate(original.posted)">{{ timeAgo(original.posted) }}</time>
            <span aria-hidden="true">·</span>
            {{ replies.length }} {{ replies.length === 1 ? "reply" : "replies" }}
          </p>
        </template>
      </div>
    </banner-4>

    <div class="container collab-body">
      <div class="thread">
        <!-- ===== Loading ===== -->
        <div v-if="loading" aria-hidden="true">
          <div v-for="n in 3" :key="n" class="c-card msg msg--skeleton">
            <span class="sk sk--avatar"></span>
            <div class="msg__main">
              <span class="sk sk--meta"></span>
              <span class="sk sk--line"></span>
              <span class="sk sk--line sk--short"></span>
            </div>
          </div>
        </div>

        <!-- ===== Not found / error ===== -->
        <div v-else-if="!original" class="c-card empty">
          <span class="material-icons empty__icon" aria-hidden="true">forum</span>
          <h3 class="empty__title">This discussion isn't available</h3>
          <p class="empty__text">It may have been removed, or the link is incorrect.</p>
          <router-link to="/questions" class="c-btn c-btn--primary">Back to all discussions</router-link>
        </div>

        <template v-else>
          <!-- ===== Original post ===== -->
          <article class="c-card msg msg--original">
            <avatar class="msg__avatar" color="#fff" :username="original.author" :backgroundColor="avatarColor(original.author)" />
            <div class="msg__main">
              <header class="msg__meta">
                <span class="msg__author">{{ original.author }}</span>
                <span v-if="original.company" class="msg__company">{{ original.company }}</span>
                <time class="msg__time" :datetime="original.postedIso" :title="fullDate(original.posted)">
                  {{ timeAgo(original.posted) }}
                </time>
              </header>
              <div class="msg__body" v-html="original.html"></div>
            </div>
          </article>

          <!-- ===== Replies ===== -->
          <div class="replies-head">
            <h2 class="replies-head__title">
              {{ replies.length ? `${replies.length} ${replies.length === 1 ? "reply" : "replies"}` : "No replies yet" }}
            </h2>
          </div>

          <ol v-if="replies.length" class="replies">
            <li
              v-for="(r, i) in replies"
              :key="r.key"
              :ref="i === replies.length - 1 ? 'lastReply' : null"
              class="c-card msg"
              :class="{ 'is-fresh': r.key === freshKey }"
            >
              <avatar class="msg__avatar msg__avatar--sm" color="#fff" :username="r.author" :backgroundColor="avatarColor(r.author)" />
              <div class="msg__main">
                <header class="msg__meta">
                  <span class="msg__author">{{ r.author }}</span>
                  <span v-if="r.isOP" class="pill pill--op">Author</span>
                  <span v-if="r.company" class="msg__company">{{ r.company }}</span>
                  <time class="msg__time" :datetime="r.postedIso" :title="fullDate(r.posted)">{{ timeAgo(r.posted) }}</time>
                </header>
                <div class="msg__body" v-html="r.html"></div>
              </div>
            </li>
          </ol>
          <p v-else class="replies-empty">Be the first to reply.</p>

          <!-- ===== Reply composer ===== -->
          <section class="c-card composer" aria-label="Write a reply">
            <div class="composer__row">
              <avatar v-if="fullName" class="msg__avatar msg__avatar--sm" color="#fff" :username="fullName" :backgroundColor="avatarColor(fullName)" />
              <div class="composer__field">
                <label for="reply" class="composer__label">Join the conversation, {{ user.FirstName || "there" }}</label>
                <textarea
                  id="reply"
                  ref="replyBox"
                  v-model="reply"
                  rows="3"
                  class="composer__message"
                  placeholder="Write a reply..."
                  @keydown.meta.enter.prevent="postNewResponse"
                  @keydown.ctrl.enter.prevent="postNewResponse"
                ></textarea>
              </div>
            </div>
            <div class="composer__footer">
              <span class="composer__hint">{{ reply.trim() ? "Ctrl + Enter to post" : `Replying to ${original.author}` }}</span>
              <button v-if="reply.trim()" type="button" class="c-btn c-btn--ghost" @click="reply = ''">Clear</button>
              <button type="button" class="c-btn c-btn--primary" :disabled="!reply.trim() || posting" @click="postNewResponse">
                <span v-if="posting" class="c-btn__spinner" aria-hidden="true"></span>
                {{ posting ? "Posting..." : "Post reply" }}
              </button>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Avatar from "../components/Atoms/Avatar.vue";
import Banner4 from "../components/Banners/banner4.vue";
import { logoutOnTokenExpired } from "@/utils/auth";

// mssql returns an array when the query has two columns with the same name (e.g. Posted)
const first = (v) => (Array.isArray(v) ? v.find((x) => x !== null && x !== undefined) : v);
const last = (v) => (Array.isArray(v) ? v[v.length - 1] ?? v[0] : v);

export default {
  name: "QuestionsSingle",
  components: { Avatar, Banner4 },

  data() {
    return {
      server: import.meta.env.VITE_APP_SERVER_URL,
      questionId: this.$route.params.id,
      questions: [],
      loading: true,
      reply: "",
      posting: false,
      freshKey: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.user || {};
    },
    fullName() {
      return `${this.user.FirstName || ""} ${this.user.LastName || ""}`.trim();
    },
    colors() {
      return this.$store.state.colors || [];
    },
    // Keep the list page's filters when going back, if we came from there
    backLink() {
      const back = window.history.state && window.history.state.back;
      return back && back.startsWith("/questions") && !back.startsWith(`/questions/${this.questionId}`) ? back : "/questions";
    },
    messages() {
      return this.questions.map((q, i) => {
        const posted = last(q.Posted);
        const author = `${first(q.FirstName) || ""} ${first(q.LastName) || ""}`.trim() || "Unknown";
        return {
          key: `${first(q.Tid) ?? "m"}-${i}-${posted}`,
          author,
          company: first(q.Name) || "",
          category: first(q.Fname) || "",
          subject: this.decodeHtml(first(q.Subject) || ""),
          posted,
          postedIso: posted ? new Date(posted).toISOString() : null,
          html: this.renderMessage(first(q.Message)),
          userId: first(q.UserId),
        };
      });
    },
    original() {
      return this.messages[0] || null;
    },
    replies() {
      const opId = this.original && this.original.userId;
      const opName = this.original && this.original.author;
      return this.messages.slice(1).map((m) => ({
        ...m,
        isOP: opId ? m.userId === opId : m.author === opName,
      }));
    },
  },

  created() {
    this.$store.commit("showNavigation");
    this.$store.commit("setActivePage", "collaborate");
  },

  mounted() {
    const top = document.getElementById("mobileScrollTarget");
    if (top) top.scrollIntoView({ behavior: "smooth" });
    this.loadThread();
  },

  methods: {
    authConfig() {
      return {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
        },
      };
    },

    async loadThread() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${this.server}/api/v1/questions/${this.questionId}`, this.authConfig());
        this.questions = Array.isArray(data) ? data : [];
      } catch (error) {
        if (error.response && error.response.status === 401) return logoutOnTokenExpired();
        console.error("Error loading discussion:", error);
        this.questions = [];
      } finally {
        this.loading = false;
      }
    },

    async postNewResponse() {
      const message = this.reply.trim();
      if (!message || this.posting || !this.original) return;
      this.posting = true;

      // The server should take the user from the JWT and the IP from the request.
      const postData = {
        UserIp: null,
        Message: message,
        EditedBy: this.user.UserName,
        Topic: this.questionId,
        UserId: this.user.UserId,
        notify: first(this.questions[0].LoweredEmail),
        fullName: this.fullName,
      };

      try {
        const { data } = await axios.post(`${this.server}/api/v1/questions/comment`, postData, this.authConfig());
        this.questions = Array.isArray(data) ? data : this.questions;
        this.reply = "";
        this.$toast.success("Your reply was posted.");

        // Briefly highlight the new reply and bring it into view
        this.$nextTick(() => {
          const newest = this.replies[this.replies.length - 1];
          this.freshKey = newest ? newest.key : null;
          const el = this.$refs.lastReply && (this.$refs.lastReply[0] || this.$refs.lastReply);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => { this.freshKey = null; }, 2500);
        });
      } catch (error) {
        if (error.response && error.response.status === 401) return logoutOnTokenExpired();
        console.error("Error posting reply:", error);
        this.$toast.error("Your reply didn't post. It's still in the box, so please try again.");
      } finally {
        this.posting = false;
      }
    },

    // ---- Display helpers ----
    decodeHtml(html) {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    },
    escapeHtml(s) {
      return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    },
    // Decode the stored text, escape it, then add links and line breaks.
    // Everything is escaped before it reaches v-html, so a message can't inject markup.
    renderMessage(raw) {
      if (!raw) return "";
      const text = this.decodeHtml(String(raw)).replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "");
      const escaped = this.escapeHtml(text);
      const linked = escaped.replace(
        /\b(https?:\/\/[^\s<]+[^\s<.,;:!?)"'\]])/gi,
        (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      );
      return linked
        .split(/\n{2,}/)
        .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
        .join("");
    },
    avatarColor(name) {
      if (!this.colors.length || !name) return undefined;
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
      return this.colors[hash % this.colors.length];
    },
    timeAgo(date) {
      return date ? this.$moment(date).subtract(1, "minutes").fromNow() : "Unknown date";
    },
    fullDate(date) {
      return date ? this.$moment(date).format("MMM D, YYYY [at] h:mm A") : "";
    },
  },
};
</script>

<style scoped>
/* Same tokens as the Collaborate list page */
.collab {
  --teal: #429da8;
  --teal-dark: #35838c;
  --teal-soft: #e6f3f4;
  --slate: #4d5967;
  --ink: #1e3040;
  --text: #3d4752;
  --muted: #7b8591;
  --line: #e4e8ec;
  --surface: #ffffff;
  --page: #f4f6f8;
  --radius: 10px;
  --shadow: 0 1px 2px rgba(30, 48, 64, 0.06), 0 1px 3px rgba(30, 48, 64, 0.08);
  color: var(--text);
}
.collab-body {
  padding-top: 24px;
  padding-bottom: 48px;
}
.thread {
  max-width: 820px;
  margin: 0 auto;
}
.c-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

/* ===== Header in the banner ===== */
.thread-head {
  max-width: 820px;
  margin: 0 auto;
  padding: 6px 0 4px;
}
.thread-head__back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  text-decoration: none;
}
.thread-head__back:hover {
  color: #fff;
  text-decoration: none;
}
.thread-head__back .material-icons {
  font-size: 18px;
}
.thread-head__tags {
  margin-bottom: 8px;
}
.thread-head__title {
  color: #fff;
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 8px;
  text-wrap: balance;
  overflow-wrap: anywhere;
}
.thread-head__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  margin: 0;
}
.thread-head__meta strong {
  color: #fff;
  font-weight: 600;
}

/* ===== Pills ===== */
.pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 10px;
  border-radius: 999px;
}
.pill--on-dark {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}
.pill--op {
  background: var(--teal-soft);
  color: var(--teal-dark);
  padding: 3px 8px;
  font-size: 11px;
}

/* ===== Messages ===== */
.msg {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
}
.msg__avatar {
  flex-shrink: 0;
}
.msg__main {
  flex: 1;
  min-width: 0;
}
.msg__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  margin-bottom: 8px;
}
.msg__author {
  font-weight: 600;
  color: var(--ink);
  font-size: 15px;
}
.msg__company {
  color: var(--muted);
  font-size: 13px;
}
.msg__time {
  margin-left: auto;
  color: var(--muted);
  font-size: 13px;
  white-space: nowrap;
}
.msg__body {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text);
  overflow-wrap: anywhere;
}
.msg__body :deep(p) {
  margin: 0 0 10px;
}
.msg__body :deep(p:last-child) {
  margin-bottom: 0;
}
.msg__body :deep(a) {
  color: var(--teal-dark);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.msg--original {
  padding: 22px 24px;
}
.msg--original .msg__body {
  font-size: 16px;
  line-height: 1.65;
  color: var(--ink);
}

/* ===== Replies ===== */
.replies-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0 12px;
}
.replies-head::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--line);
}
.replies-head__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0;
}
.replies {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.replies .msg {
  padding: 14px 18px;
  transition: box-shadow 0.4s, border-color 0.4s;
}
.replies .msg.is-fresh {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(66, 157, 168, 0.15), var(--shadow);
}
.replies-empty {
  color: var(--muted);
  font-size: 14px;
  margin: 0 0 12px;
}

/* ===== Composer ===== */
.composer {
  margin-top: 20px;
  padding: 16px 18px;
}
.composer__row {
  display: flex;
  gap: 12px;
}
.composer__field {
  flex: 1;
  min-width: 0;
}
.composer__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 8px;
}
.composer__message {
  width: 100%;
  border: 1px solid var(--line);
  background: var(--page);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text);
  resize: vertical;
  outline: 0;
  transition: background 0.15s, border-color 0.15s;
}
.composer__message:focus {
  background: #fff;
  border-color: #cfd6dd;
}
.composer__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.composer__hint {
  margin-right: auto;
  font-size: 13px;
  color: var(--muted);
}

/* ===== Buttons ===== */
.c-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, opacity 0.15s;
}
.c-btn--primary {
  background: var(--teal);
  color: #fff;
}
.c-btn--primary:hover:not(:disabled) {
  background: var(--teal-dark);
  color: #fff;
  text-decoration: none;
}
.c-btn--primary:disabled {
  opacity: 0.45;
  cursor: default;
}
.c-btn--ghost {
  background: transparent;
  color: var(--slate);
}
.c-btn--ghost:hover {
  background: var(--page);
}
.c-btn__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ===== Empty ===== */
.empty {
  text-align: center;
  padding: 48px 24px;
}
.empty__icon {
  font-size: 44px;
  color: var(--teal);
  background: var(--teal-soft);
  border-radius: 50%;
  padding: 16px;
}
.empty__title {
  font-size: 19px;
  font-weight: 700;
  color: var(--ink);
  margin: 16px 0 6px;
}
.empty__text {
  color: var(--muted);
  margin: 0 0 20px;
}

/* ===== Skeleton ===== */
.msg--skeleton {
  margin-bottom: 10px;
}
.sk {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f4 25%, #f6f8fa 50%, #eef1f4 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}
.sk--on-dark {
  background: linear-gradient(90deg, rgba(255,255,255,0.12) 25%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.12) 75%);
  background-size: 200% 100%;
}
.sk--avatar { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; }
.sk--pill { width: 80px; height: 20px; border-radius: 999px; margin-bottom: 12px; }
.sk--h1 { width: 70%; height: 28px; margin-bottom: 8px; }
.sk--meta { width: 35%; height: 12px; margin-bottom: 12px; }
.sk--line { width: 100%; height: 12px; margin-bottom: 8px; }
.sk--short { width: 60%; }

@media (max-width: 575px) {
  .msg,
  .msg--original {
    padding: 14px;
    gap: 10px;
  }
  .msg__avatar--sm {
    display: none;
  }
  .msg__time {
    margin-left: 0;
    width: 100%;
  }
}

@keyframes shimmer { to { background-position: -200% 0; } }
@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .collab *,
  .collab *::before,
  .collab *::after {
    animation: none !important;
    transition: none !important;
  }
}
</style>
